import {
  Dispatch,
  ReactNode,
  SetStateAction,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import {useFetchSimilarResidents} from "../../page-admin-residents/hooks/useFetchSimilarResidents";
import {Resident} from "../../page-admin-residents/types";
import {PccPatient} from "../../../services/PccService";
import {SearchSimilarResidents} from "../../../services/ResidentServices";
import {FetchStatusReturnType} from "../atoms/SimilarResidents/types";

type SimilarResidentsContextReturnType = {
  fetching: FetchStatusReturnType;
  isPointClickCareEnabled: boolean;
  pccResident: PccPatient;
  setPccResident: Dispatch<SetStateAction<PccPatient>>;
  openDemographics: boolean;
  setOpenDemographics: Dispatch<SetStateAction<boolean>>;
  presetLinkedResident?: Resident;
};

const SimilarResidentsContext =
  createContext<SimilarResidentsContextReturnType>(undefined);

export const SimilarResidentsContextProvider = ({
  children,
  isPointClickCareEnabled,
  presetLinkedResident,
}: {
  children: ReactNode;
  isPointClickCareEnabled: boolean;
  /** Resident chosen in Start Workflow — treated as a 100% duplicate match without list-duplicates. */
  presetLinkedResident?: Resident;
}) => {
  const [pccResident, setPccResident] = useState<PccPatient>();

  const [openDemographics, setOpenDemographics] = useState(
    () => !presetLinkedResident,
  );

  const {
    data: similarResidentsFromApi,
    mutate,
    isLoading: isFetchingSimilarResidents,
    reset: clearSimilarResidentsData,
  } = useFetchSimilarResidents();

  const presetSimilarResidents = useMemo(
    () =>
      presetLinkedResident
        ? [{resident: presetLinkedResident, match: "100%"}]
        : undefined,
    [presetLinkedResident],
  );

  const similarResidents = similarResidentsFromApi ?? presetSimilarResidents;

  const fetchSimilarResidents = (body: SearchSimilarResidents) => mutate(body);

  // Auto-toggle only when list-duplicates API returns — not for preset Start Workflow links.
  useEffect(() => {
    if (!similarResidentsFromApi) {
      return;
    }

    if (similarResidentsFromApi.length) {
      const hasTotalMatch = similarResidentsFromApi.some(
        item => item.match === "100%",
      );

      setOpenDemographics(!hasTotalMatch);
    } else {
      setOpenDemographics(false);
    }
  }, [isFetchingSimilarResidents, similarResidentsFromApi]);

  return (
    <SimilarResidentsContext.Provider
      value={{
        fetching: {
          similarResidents,
          fetchSimilarResidents,
          isFetchingSimilarResidents,
          clearSimilarResidentsData,
          similarResidentsFound: Boolean(similarResidents?.length),
        },
        isPointClickCareEnabled,
        setPccResident,
        pccResident,
        openDemographics,
        setOpenDemographics,
        presetLinkedResident,
      }}
    >
      {children}
    </SimilarResidentsContext.Provider>
  );
};

export const useSimilarResidentsContext =
  (): SimilarResidentsContextReturnType => {
    const context = useContext(SimilarResidentsContext);

    if (context === undefined) {
      throw new Error(
        "useSimilarResidentsContext must be used within SimilarResidentsContextProvider",
      );
    }

    return context;
  };
